import { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * SendBackToTesterButton — Iter180 MCC bounce-back affordance.
 *
 * FOH-only action on the pricing screen. Sends the linked intake
 * session back to the tester's MCC queue with an optional reason so
 * the tester can complete (or re-do) MCC capture for the DVSA defects.
 *
 * Re-runs MCC classification server-side, so policy bumps (e.g.
 * iter180's advisory → LEVEL 3) surface cards that weren't requested
 * on the original pass. Existing tester captures are preserved.
 */
export default function SendBackToTesterButton({ yestimateId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!yestimateId) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/foh/yestimate/${yestimateId}/send-to-tester-for-mcc`,
        { reason: reason.trim() || null },
      );
      setResult(r.data?.result);
      setReason("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send to tester");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setReason("");
    setResult(null);
    setError(null);
  };

  if (!yestimateId) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-purple-700 border-purple-300 hover:bg-purple-50"
        data-testid="foh-send-back-to-tester-btn"
        title="Bounce this job back to the tester so they can complete MOT Context Capture"
      >
        <Send className="w-3.5 h-3.5 mr-1.5" />
        Send to Tester for MCC
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="sm:max-w-[480px]" data-testid="foh-send-back-dialog">
          <DialogHeader>
            <DialogTitle>Send back to tester for MCC</DialogTitle>
            <DialogDescription className="text-sm text-slate-600 leading-snug">
              The tester will see this job in Today's MOTs with the cards
              they need to complete. Existing captures are preserved —
              this only fills the gaps.
            </DialogDescription>
          </DialogHeader>

          {!result && !error && (
            <div className="space-y-2 py-2">
              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                Reason (optional)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Need wear pattern + measurement on OSF disc before I can price the pair."
                rows={3}
                maxLength={500}
                className="text-sm"
                data-testid="foh-send-back-reason"
              />
              <p className="text-[11px] text-slate-400">
                {reason.length}/500 characters
              </p>
            </div>
          )}

          {result && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 flex items-start gap-2"
              data-testid="foh-send-back-success"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-semibold text-emerald-900">
                  Sent to tester for MCC
                </p>
                <p className="text-[12px] text-emerald-800 leading-snug mt-0.5">
                  {result.pending_entries} card(s) pending
                  {result.added_entries > 0 && (
                    <> — {result.added_entries} new card(s) added by the
                    latest MCC policy</>
                  )}
                  . The tester will see this in Today's MOTs.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 flex items-start gap-2"
              data-testid="foh-send-back-error"
            >
              <AlertCircle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-red-800 leading-snug">{error}</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={close}
              disabled={submitting}
              data-testid="foh-send-back-cancel-btn"
            >
              {result ? "Close" : "Cancel"}
            </Button>
            {!result && (
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-purple-700 hover:bg-purple-800 text-white"
                data-testid="foh-send-back-confirm-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
                ) : (
                  <><Send className="w-3.5 h-3.5 mr-1.5" />Send to tester</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
